import { Button } from "@/components/ui";
import type { PoolSearch } from "@/hooks/usePoolSearch";
import { leafLabel } from "@/lib/attributeReview/kindWords";
import { box, muted } from "../ReviewDrawer/agentStyles";
import type { Pool } from "./PoolSection";
import { PoolValueList } from "./PoolValueList";

export type TextPoolProps = {
  picked: string | null;
  onPick: (key: string) => void;
  search: PoolSearch;
};

type Props = TextPoolProps & { pool: Pool | null; readOnly: boolean };

export function TextFieldPool({
  pool,
  picked,
  onPick,
  search,
  readOnly,
}: Props) {
  if (pool && (readOnly || (pool.source === "existing" && !picked)))
    return (
      <PoolValueList
        pool={pool}
        canSwitch={!readOnly}
        setSellerTyping={search.setSellerTyping}
      />
    );
  if (readOnly)
    return (
      <p className="text-[13px] text-slate-500 dark:text-slate-400">
        This field has no list. The seller types the answer.
      </p>
    );
  const chosen = picked ?? pool?.key ?? null;
  return (
    <div className={box}>
      <label className="block font-semibold" htmlFor="attach-list-search">
        Attach a list
      </label>
      <p className={`text-xs ${muted}`}>
        The store box is free text. With a list, the seller picks from it on
        every store that uses this field.
      </p>
      {chosen && (
        <p>
          {picked ? "Attaching" : "Suggested"}:{" "}
          <b title={chosen}>{leafLabel(chosen)}</b>. Its values show after you
          save.
        </p>
      )}
      <input
        id="attach-list-search"
        value={search.text}
        onChange={(e) => search.setText(e.target.value)}
        placeholder="Search lists by name"
        className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
      />
      {search.keys.map((key) => (
        <div
          key={key}
          className="flex items-center justify-between gap-2 border-t border-slate-100 pt-1.5 dark:border-slate-800"
        >
          <p className="font-medium" title={key}>
            {leafLabel(key)}
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={key === chosen}
            onClick={() => onPick(key)}
          >
            {key === chosen ? "Using" : "Use"}
          </Button>
        </div>
      ))}
    </div>
  );
}
